import * as Yup from 'yup';

import { ALLOWED_CHARACTERS_REGEX, MAX_FILE_SIZE_5MB } from '@/common/constants';

export const invoiceShipOwnerSchema = (isDraft: boolean, role: string, savedOthersFileIds: string[] = [], statusKursConversion = false, isPPN = false) => {

    const validateOther = (otherNumber: number, message = `All fields in this other group are required if any is filled`) => {
        return Yup.string().test('all-or-none', message, function () {
            const { parent } = this;
            const other = parent[`other${otherNumber}`];
            const otherFeeRaw = parent[`otherFee${otherNumber}`];
            const otherFee = Boolean(otherFeeRaw) && !(["0", "Rp0", "IDR0", "$0", "USD0"].includes(otherFeeRaw));
            const otherCostFile = parent[`otherCostFile${otherNumber}`];
            const anyFilled = Boolean(other || otherFee || otherCostFile);
            const allFilled = Boolean(other && otherFee && (otherCostFile || savedOthersFileIds[otherNumber-1]));

            //console.log([otherNumber, other, otherFee, otherCostFile, savedOthersFileIds[otherNumber-1]])

            if (!anyFilled) return true;
            return allFilled;
        });
    };

    return (
        Yup.object().shape({
            type: Yup.string()
                .required('Type is required'),
            invoiceID: role === 'admin finance' ?
                Yup.string()
                    .trim()
                    .required('Invoice ID is required')
                    .matches(ALLOWED_CHARACTERS_REGEX, 'Input must only contain alphabets, numbers, commas, dots, slashes, and dashes.')
                    .max(50, 'Max length is 50 characters') :
                Yup.string()
                    .nullable()
                    .trim()
                    .matches(ALLOWED_CHARACTERS_REGEX, 'Input must only contain alphabets, numbers, commas, dots, slashes, and dashes.')
                    .max(50, 'Max length is 50 characters'),
            companyName: Yup.string()
                .trim()
                .required('Company name is required'),
            shipName: Yup.string()
                .nullable()
                .required('Ship name is required'),
            jobDescription: Yup.string()
                .trim()
                .required('Job description is required')
                .matches(ALLOWED_CHARACTERS_REGEX, 'Input must only contain alphabets, numbers, commas, dots, slashes, and dashes.')
                .max(255, 'Max length is 255 characters'),
            invoiceDate: Yup.date()
                .typeError('Invalid date entered')
                .required('Invoice date is required'),
                //.when('inspectionDate', (inspectionDate, schema) => (schema.min(inspectionDate, 'Invoice date cannot be earlier than inspection date'))),
            inspectionDate: Yup.date()
                .typeError('Invalid date entered')
                .required('Inspection date is required'),
            country: Yup.string()
                .required('Country is required'),
            port: Yup.string()
                .trim()
                .required('Port is required')
                .matches(ALLOWED_CHARACTERS_REGEX, 'Input must only contain alphabets, numbers, commas, dots, slashes, and dashes.')
                .max(50, 'Max length is 50 characters'),
            fee: Yup.string()
                .required('Fee is required'),
            inspectionFee: isDraft ? Yup.string() : Yup.string()
                .required('Inspection fee is required'),
            other1: validateOther(1),
            otherFee1: validateOther(1),
            otherCostFile1: validateOther(1),
            other2: validateOther(2),
            otherFee2: validateOther(2),
            otherCostFile2: validateOther(2),
            other3: validateOther(3),
            otherFee3: validateOther(3),
            otherCostFile3: validateOther(3),
            other4: validateOther(4),
            otherFee4: validateOther(4),
            otherCostFile4: validateOther(4),
            other5: validateOther(5),
            otherFee5: validateOther(5),
            otherCostFile5: validateOther(5),
            other6: validateOther(6),
            otherFee6: validateOther(6),
            otherCostFile6: validateOther(6),
            other7: validateOther(7),
            otherFee7: validateOther(7),
            otherCostFile7: validateOther(7),
            other8: validateOther(8),
            otherFee8: validateOther(8),
            otherCostFile8: validateOther(8),
            other9: validateOther(9),
            otherFee9: validateOther(9),
            otherCostFile9: validateOther(9),
            other10: validateOther(10),
            otherFee10: validateOther(10),
            otherCostFile10: validateOther(10),
            faktur: (role === 'admin finance' && isPPN) ?
                Yup.mixed()
                    .required('Faktur file is required')
                    .test('fileSize', 'File size is too large', (value) => {
                        if (value && (value as File).size > MAX_FILE_SIZE_5MB) {
                            return false;
                        }
                        return true;
                    }) : Yup.mixed()
                        .nullable()
                        .test('fileSize', 'File size is too large', (value) => {
                            if (value && (value as File).size > MAX_FILE_SIZE_5MB) {
                                return false;
                            }
                            return true;
                        }),
            SKTDFile: Yup.mixed().when(['isIndonesia'], {
                is: (isIndonesia: boolean) => (isIndonesia && !isPPN),
                then: (schema) => schema.required('SKTD file is required when PPN is disabled'),
                otherwise: (schema) => schema.notRequired()
            }),
            targetExchangeRate: statusKursConversion ? Yup.string().required('Target exchange rate is required') : Yup.string().nullable(),
            conversionKurs: statusKursConversion ? Yup.string().required('Conversion kurs is required') : Yup.string().nullable(),
            conversionAmount: statusKursConversion ? Yup.string().required('Conversion amount is required') : Yup.string().nullable(),
            conversionNotes: statusKursConversion ? Yup.string().required('Conversion notes is required') : Yup.string().nullable(),
            conversionUploadFile: statusKursConversion ?
                Yup.mixed()
                    .required('Conversion file is required')
                    .test('fileSize', 'File size is too large', (value) => {
                        if (value && (value as File).size > MAX_FILE_SIZE_5MB) {
                            return false;
                        }
                        return true;
                    }) : Yup.mixed()
                        .nullable()
                        .test('fileSize', 'File size is too large', (value) => {
                            if (value && (value as File).size > MAX_FILE_SIZE_5MB) {
                                return false;
                            }
                            return true;
                        }),
        }));
}
